import { useNavigate } from "react-router-dom";
import { ArrowLeft, Share, Plus, MoreVertical, Download, CheckCircle, Smartphone } from "lucide-react";

const GOLD = "#C9A84C";

const IPHONE_STEPS = [
  {
    icon: <Smartphone size={20} />,
    title: "Open Safari",
    desc: "Zorg dat je de website opent in Safari. De installatie werkt niet vanuit Chrome of een andere browser op iPhone.",
    accent: false,
  },
  {
    icon: <Share size={20} />,
    title: "Tik op het Deel-icoon",
    desc: "Tik op het deel-icoon onderaan je scherm — het vierkantje met een pijltje omhoog.",
    accent: true,
  },
  {
    icon: <Plus size={20} />,
    title: "Kies 'Zet op beginscherm'",
    desc: "Scroll in het menu omlaag en tik op 'Zet op beginscherm' (of 'Add to Home Screen').",
    accent: false,
  },
  {
    icon: <CheckCircle size={20} />,
    title: "Tik op 'Voeg toe'",
    desc: "Tik rechts bovenin op 'Voeg toe'. FitSmith verschijnt nu als app-icoon op je beginscherm.",
    accent: true,
  },
];

const ANDROID_STEPS = [
  {
    icon: <Smartphone size={20} />,
    title: "Open Chrome",
    desc: "Zorg dat je de website opent in Google Chrome. De installatiefunctie werkt het beste in Chrome.",
    accent: false,
  },
  {
    icon: <MoreVertical size={20} />,
    title: "Tik op de drie puntjes",
    desc: "Tik op de drie puntjes (⋮) rechtsboven in Chrome om het menu te openen.",
    accent: true,
  },
  {
    icon: <Download size={20} />,
    title: "Kies 'Installeer app' of 'Voeg toe aan beginscherm'",
    desc: "Tik op 'Installeer app' of 'Voeg toe aan beginscherm'. Soms verschijnt er ook automatisch een installatiebalk onderaan.",
    accent: false,
  },
  {
    icon: <CheckCircle size={20} />,
    title: "Bevestig de installatie",
    desc: "Tik op 'Installeer' of 'Toevoegen'. FitSmith staat nu als app op je telefoon!",
    accent: true,
  },
];

function StepCard({ step, index, total }) {
  return (
    <div className="flex gap-4">
      {/* Verticale lijn + nummer */}
      <div className="flex flex-col items-center">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-black text-sm"
          style={step.accent
            ? { background: GOLD, color: "#000" }
            : { background: "rgba(255,255,255,0.06)", color: "#71717a", border: "1px solid rgba(255,255,255,0.08)" }
          }
        >
          {index + 1}
        </div>
        {index < total - 1 && (
          <div className="w-px flex-1 mt-2" style={{ background: "rgba(255,255,255,0.06)", minHeight: "24px" }} />
        )}
      </div>

      {/* Content */}
      <div className="pb-7 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span style={{ color: step.accent ? GOLD : "#52525b" }}>{step.icon}</span>
          <h3 className="text-white font-bold text-sm">{step.title}</h3>
        </div>
        <p className="text-zinc-500 text-sm leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

function DeviceSection({ platform, emoji, steps, label }) {
  return (
    <div className="bg-[#0f0f0f] border border-white/6 rounded-3xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-white/5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {emoji}
        </div>
        <div>
          <p className="text-[#C9A84C] text-[10px] font-black uppercase tracking-widest mb-0.5">{label}</p>
          <h2 className="text-white font-black text-lg">{platform}</h2>
        </div>
      </div>

      {/* Stappen */}
      <div className="px-6 pt-6">
        {steps.map((step, i) => (
          <StepCard key={i} step={step} index={i} total={steps.length} />
        ))}
      </div>
    </div>
  );
}

export default function AppInstallPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Topbar */}
      <div className="sticky top-0 z-20 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="w-9 h-9 rounded-xl bg-[#141414] border border-white/6 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <p className="text-[#C9A84C] text-[10px] font-black uppercase tracking-widest leading-none mb-0.5">FitSmith</p>
          <h1 className="text-white font-black text-base leading-none">Gebruik als app</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8 space-y-5">
        {/* Intro card */}
        <div className="rounded-3xl px-6 py-6 text-center"
          style={{ background: "linear-gradient(135deg, #C9A84C12, #C9A84C06)", border: "1px solid #C9A84C25" }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: "#C9A84C15", border: "1.5px solid #C9A84C30" }}>
            <Smartphone size={28} style={{ color: GOLD }} />
          </div>
          <h2 className="text-white font-black text-xl mb-2">Installeer FitSmith als app</h2>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
            Geen app store nodig. Voeg FitSmith toe aan je beginscherm en open je schema's direct — net als een echte app.
          </p>

          {/* Voordelen */}
          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { emoji: "⚡", label: "Sneller starten" },
              { emoji: "📱", label: "Volledig scherm" },
              { emoji: "🔒", label: "Altijd beschikbaar" },
            ].map(({ emoji, label }) => (
              <div key={label} className="bg-white/4 border border-white/6 rounded-2xl py-3 px-2">
                <div className="text-xl mb-1">{emoji}</div>
                <p className="text-zinc-400 text-xs font-medium leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* iPhone instructies */}
        <DeviceSection
          platform="iPhone"
          emoji="🍎"
          label="iOS · Safari"
          steps={IPHONE_STEPS}
        />

        {/* Android instructies */}
        <DeviceSection
          platform="Android"
          emoji="🤖"
          label="Android · Chrome"
          steps={ANDROID_STEPS}
        />

        {/* Footer tip */}
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl px-5 py-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/4 flex items-center justify-center shrink-0 mt-0.5">
            <CheckCircle size={14} style={{ color: GOLD }} />
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-0.5">Klaar om te beginnen?</p>
            <p className="text-zinc-500 text-xs leading-relaxed">
              Na installatie open je FitSmith direct via het app-icoon op je beginscherm. Je schema's, trainingen en voortgang zijn altijd bereikbaar.
            </p>
          </div>
        </div>

        {/* Terug knop */}
        <button
          onClick={() => navigate("/")}
          data-testid="go-home-btn"
          className="w-full py-4 rounded-2xl border border-white/10 text-zinc-400 text-sm font-bold hover:bg-white/4 transition-colors"
        >
          Terug naar de website
        </button>
      </div>
    </div>
  );
}
